import React, { useEffect, useRef, useState } from 'react';
import { Alert, Box, ButtonBase, Chip, InputAdornment, MenuItem, MenuList, Popover, Stack, TextField, Typography } from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import RemoveIcon from '@mui/icons-material/Remove';
import SearchIcon from '@mui/icons-material/Search';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
import { TFunction } from 'i18next';
import { useTranslation } from 'react-i18next';
import { BillingItemTypeEnum, DiscountModeEnum } from '../../common/enums/invoicing.enum';
import { BillingItemDto, BillingSchemeItemInputDto } from '../../common/interfaces/invoicing.interface';
import { calculateLineTotals, formatMoney, sumLineTotals } from '../../utilities/helper/money.helper';
import { discountAmounts, discountProblems, DiscountProblem, parseDiscountValue } from '../../utilities/helper/discount.helper';
import { palette } from '../../theme';
import GenericInfoModal from '../GenericInfoModal';
import RowActionsMenu from '../RowActionsMenu/RowActionsMenu';
import DiscountRow, { DiscountState, newDiscount } from './DiscountRow';
import { InlineAction, LabelDialog, LINE_COLUMNS, LineTableHeader, PriceField, VatDialog } from './LineTable';

/**
 * A line in the editor: what to send back, plus the catalogue values it inherits when
 * it overrides nothing, so the row can show both without another lookup.
 */
export interface BillingLineState extends BillingSchemeItemInputDto {
  name: string;
  type: BillingItemTypeEnum;
  catalogPrice: number;
  catalogVat: number;
  catalogLabel: string;
  /** The item was deactivated in the catalogue after it was added here. */
  isInactive: boolean;
}

const MUTED = palette.text.secondary;
const TINT = palette.surface.tint;

/** The inputs the design draws here: 44px tall. Outline and focus come from the theme. */
export const fieldSx = {
  '& .MuiOutlinedInput-root': {
    height: 44,
    borderRadius: '10px',
    backgroundColor: palette.surface.main
  },
  '& .MuiOutlinedInput-input': { fontSize: 16, px: '12px' }
} as const;

export const textButtonSx = {
  color: 'primary.main',
  fontSize: 13,
  p: '4px 8px',
  borderRadius: 2,
  '&:hover': { backgroundColor: TINT }
} as const;

/**
 * What the price means, spelled out under the name.
 *
 * Without it a per-attendance-day price reads as a monthly one, and an arrangement gets
 * built out of numbers that were never comparable.
 */
export const unitHint = (t: TFunction, type: BillingItemTypeEnum, defaultQuantity?: number | null): string =>
  type === BillingItemTypeEnum.ATTENDANCE_BASED
    ? t('billing.lines.perAttendanceDay')
    : defaultQuantity && defaultQuantity !== 1
      ? t('billing.lines.timesMonthly', { n: defaultQuantity })
      : t('billing.lines.monthlyUnit');

export const toLineState = (item: BillingItemDto): BillingLineState => ({
  billingItemId: item.id,
  quantity: item.type === BillingItemTypeEnum.ATTENDANCE_BASED ? 1 : item.defaultQuantity,
  unitPriceNetOverride: null,
  vatRateOverride: null,
  pdfLabelOverride: null,
  sortOrder: 0,
  name: item.name,
  type: item.type,
  catalogPrice: item.unitPriceNet,
  catalogVat: item.vatRate,
  catalogLabel: item.pdfLabel,
  // the add menu only offers active items, so anything added here is active
  isInactive: false
});

const lineTotals = (line: BillingLineState, vatApplicable: boolean) =>
  calculateLineTotals(
    line.unitPriceNetOverride ?? line.catalogPrice,
    line.quantity ?? 1,
    vatApplicable ? (line.vatRateOverride ?? line.catalogVat) : 0
  );

/** A label sitting above its field, as the design draws every input here. */
export const FieldLabel: React.FC<{ htmlFor: string; id?: string; children: React.ReactNode }> = ({ htmlFor, id, children }) => (
  <Typography component="label" htmlFor={htmlFor} id={id} sx={{ display: 'block', fontSize: 13, color: MUTED, mb: 0.75 }}>
    {children}
  </Typography>
);


/** "+ Add item": a searchable list of what the catalogue still has to offer. */
export const AddItemMenu: React.FC<{ available: BillingItemDto[]; onPick: (item: BillingItemDto) => void; label?: string }> = ({ available, onPick, label }) => {
  const { t } = useTranslation();
  const anchor = useRef<HTMLButtonElement>(null);
  const list = useRef<HTMLUListElement>(null);
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');
  const visible = available.filter(item => item.name.toLowerCase().includes(query.trim().toLowerCase()));

  const close = () => {
    setOpen(false);
    setQuery('');
  };

  return (
    <>
      <ButtonBase
        ref={anchor}
        onClick={() => setOpen(true)}
        aria-haspopup="dialog"
        aria-expanded={open}
        sx={{
          height: 44,
          px: 1.5,
          gap: 0.75,
          borderRadius: '10px',
          fontSize: 16,
          color: 'primary.main',
          '&:hover': { backgroundColor: TINT }
        }}
      >
        <AddIcon sx={{ fontSize: 20 }} />
        {label ?? t('billing.lines.addItem')}
      </ButtonBase>
      <Popover
        open={open}
        anchorEl={anchor.current}
        onClose={close}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
        slotProps={{
          paper: { elevation: 3, sx: { borderRadius: '10px', width: anchor.current?.parentElement?.clientWidth || 480, maxWidth: '100%' } }
        }}
      >
        <Box sx={{ p: 1.5, pb: 1 }}>
          <TextField
            autoFocus
            fullWidth
            value={query}
            placeholder={t('billing.lines.searchItems')}
            onChange={event => setQuery(event.target.value)}
            onKeyDown={event => {
              // into the list, so the arrows keep working from where the typing happened
              if (event.key === 'ArrowDown') {
                event.preventDefault();
                (list.current?.querySelector('[role="menuitem"]') as HTMLElement | null)?.focus();
              }
            }}
            slotProps={{
              htmlInput: { 'aria-label': t('billing.lines.searchItems') },
              input: {
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon sx={{ fontSize: 20, color: MUTED }} />
                  </InputAdornment>
                )
              }
            }}
            sx={{ '& .MuiOutlinedInput-root': { height: 40, borderRadius: '10px' } }}
          />
        </Box>
        {!visible.length ? (
          <Typography sx={{ fontSize: 14, color: MUTED, px: 2, pb: 2 }}>
            {available.length ? t('common.noResults') : t('billing.lines.allAdded')}
          </Typography>
        ) : (
          <MenuList ref={list} sx={{ maxHeight: 320, overflowY: 'auto', pt: 0 }}>
            {visible.map(item => (
              <MenuItem
                key={item.id}
                onClick={() => {
                  onPick(item);
                  close();
                }}
                sx={{ height: 52, px: 2, justifyContent: 'space-between', gap: 2 }}
              >
                <Box sx={{ minWidth: 0 }}>
                  <Typography sx={{ fontSize: 15 }} noWrap>
                    {item.name}
                  </Typography>
                  <Typography sx={{ fontSize: 12, color: MUTED }}>{unitHint(t, item.type, item.defaultQuantity)}</Typography>
                </Box>
                <Typography sx={{ fontSize: 14, fontVariantNumeric: 'tabular-nums', whiteSpace: 'nowrap' }}>
                  {formatMoney(item.unitPriceNet)}
                </Typography>
              </MenuItem>
            ))}
          </MenuList>
        )}
      </Popover>
    </>
  );
};


/** What a line costs before VAT for a month: price x quantity, or one day's price. */
const lineNet = (line: BillingLineState) => calculateLineTotals(line.unitPriceNetOverride ?? line.catalogPrice, line.quantity ?? 1).net;

/** The discounts on one line, in order, and what each takes off it (null per day). */
const discountsOn = (line: BillingLineState, discounts: DiscountState[]) => {
  const own = discounts.filter(discount => discount.appliesTo === line.billingItemId);
  if (line.type === BillingItemTypeEnum.ATTENDANCE_BASED) return own.map(discount => ({ discount, amount: null as number | null }));
  const amounts = discountAmounts(
    lineNet(line),
    own.map(discount => ({ mode: discount.mode, value: parseDiscountValue(discount.valueText) }))
  );
  return own.map((discount, index) => ({ discount, amount: amounts[index] as number | null }));
};

/**
 * The monthly figure of a set of lines: the fixed ones, less their discounts. Lines and
 * discounts counted per attendance day are not in it - their amount depends on the month -
 * and are named instead.
 */
export const billingMonthly = (
  lines: BillingLineState[],
  discounts: DiscountState[],
  vatApplicable: boolean
): { gross: number; net: number; discount: number; perDay: string[] } => {
  const fixed = lines.filter(line => line.type === BillingItemTypeEnum.FIXED_QUANTITY);
  const totals = sumLineTotals(fixed.map(line => lineTotals(line, vatApplicable)));
  const discount = fixed.reduce(
    (sum, line) => sum + discountsOn(line, discounts).reduce((inner, entry) => inner + (entry.amount ?? 0), 0),
    0
  );
  const rate = (line: BillingLineState) => (vatApplicable ? (line.vatRateOverride ?? line.catalogVat) : 0);
  const discountGross = fixed.reduce(
    (sum, line) =>
      sum + discountsOn(line, discounts).reduce((inner, entry) => inner + calculateLineTotals(entry.amount ?? 0, 1, rate(line)).gross, 0),
    0
  );
  return {
    gross: Math.round((totals.gross - discountGross) * 100) / 100,
    net: Math.round((totals.net - discount) * 100) / 100,
    discount,
    perDay: lines.filter(line => line.type === BillingItemTypeEnum.ATTENDANCE_BASED).map(line => line.pdfLabelOverride || line.catalogLabel || line.name)
  };
};

/** Every discount's problems, keyed by its key; empty when they can all be saved. */
export const billingDiscountProblems = (
  t: TFunction,
  lines: BillingLineState[],
  discounts: DiscountState[]
): Record<string, DiscountProblem> => {
  const result: Record<string, DiscountProblem> = {};
  lines.forEach(line => {
    const own = discounts.filter(discount => discount.appliesTo === line.billingItemId);
    const problems = discountProblems(
      t as any,
      line.type === BillingItemTypeEnum.ATTENDANCE_BASED ? null : lineNet(line),
      own
    );
    own.forEach((discount, index) => {
      if (problems[index].label || problems[index].value) result[discount.key] = problems[index];
    });
  });
  return result;
};

interface BillingLinesEditorProps {
  lines: BillingLineState[];
  onChange: (lines: BillingLineState[]) => void;
  /** Active catalogue items; anything already in `lines` is left out of the add menu. */
  catalog: BillingItemDto[];
  vatApplicable: boolean;
  /** Wording for the "inactive items" banner, which differs per screen. */
  inactiveNotice?: string;
  /** Kept for callers; the layout is the same at every width. */
  wide?: boolean;
  /** Per line, keyed by billing item: a quantity or price the form refuses. */
  lineErrors?: Record<string, string>;
  /** Under the list, e.g. "add at least one item". */
  listError?: string;
  /** A panel shows one total in its footer instead. */
  showTotal?: boolean;
  /** Given only where discounts are allowed: a child's arrangement, not a group's price list. */
  discounts?: DiscountState[];
  onDiscountsChange?: (discounts: DiscountState[]) => void;
  /** After a save attempt, every discount problem shows, touched or not. */
  showErrors?: boolean;
}

/**
 * Shared by the group price list and a child's own price: one row per catalogue item -
 * item, price, quantity, total - and whatever else a line can do in its menu.
 *
 * The price field holds the real price, the catalogue's until it is changed, because a
 * placeholder reads as "no price yet". Typing the catalogue price back in stores null
 * rather than the number: the line then follows the catalogue when its price changes.
 *
 * Where discounts are allowed, each sits right under the item it lowers, as it will on the
 * invoice. Removing an item takes its discounts with it, after saying so.
 */
const BillingLinesEditor: React.FC<BillingLinesEditorProps> = ({
  lines,
  onChange,
  catalog,
  vatApplicable,
  inactiveNotice,
  lineErrors,
  listError,
  showTotal = true,
  discounts,
  onDiscountsChange,
  showErrors
}) => {
  const { t } = useTranslation();
  const priceInputs = useRef(new Map<string, HTMLInputElement>());
  const discountLabels = useRef(new Map<string, HTMLInputElement>());
  const [justAdded, setJustAdded] = useState<string | null>(null);
  const [focusDiscount, setFocusDiscount] = useState<string | null>(null);
  const [labelFor, setLabelFor] = useState<BillingLineState | null>(null);
  const [vatFor, setVatFor] = useState<BillingLineState | null>(null);
  const [removing, setRemoving] = useState<BillingLineState | null>(null);
  const allowDiscounts = !!discounts && !!onDiscountsChange;
  const allDiscounts = discounts ?? [];

  const setLine = (billingItemId: string, patch: Partial<BillingLineState>) =>
    onChange(lines.map(line => (line.billingItemId === billingItemId ? { ...line, ...patch } : line)));

  const addLine = (item: BillingItemDto) => {
    if (lines.some(line => line.billingItemId === item.id)) return;
    onChange([...lines, { ...toLineState(item), sortOrder: lines.length }]);
    setJustAdded(item.id);
  };

  const removeLine = (line: BillingLineState) => {
    onChange(lines.filter(other => other.billingItemId !== line.billingItemId));
    if (allowDiscounts) onDiscountsChange!(allDiscounts.filter(discount => discount.appliesTo !== line.billingItemId));
  };

  const addDiscount = (target?: BillingLineState) => {
    const on = target ?? lines.find(line => line.type === BillingItemTypeEnum.FIXED_QUANTITY) ?? lines[0];
    if (!on || !allowDiscounts) return;
    const discount = newDiscount(on.billingItemId);
    onDiscountsChange!([...allDiscounts, discount]);
    setFocusDiscount(discount.key);
  };

  const setDiscount = (key: string, patch: Partial<DiscountState>) =>
    onDiscountsChange?.(allDiscounts.map(discount => (discount.key === key ? { ...discount, ...patch } : discount)));

  // the new line is at the bottom of the list; its price is the first thing to decide
  useEffect(() => {
    if (!justAdded) return;
    priceInputs.current.get(justAdded)?.focus();
    setJustAdded(null);
  }, [justAdded, lines]);

  useEffect(() => {
    if (!focusDiscount) return;
    discountLabels.current.get(focusDiscount)?.focus();
    setFocusDiscount(null);
  }, [focusDiscount, discounts]);

  const available = catalog
    .filter(item => !lines.some(line => line.billingItemId === item.id))
    .sort((a, b) => a.name.localeCompare(b.name));

  const problems = allowDiscounts ? billingDiscountProblems(t, lines, allDiscounts) : {};
  const monthly = billingMonthly(lines, allDiscounts, vatApplicable);
  const targets = lines.map(line => ({ key: line.billingItemId, name: line.name }));

  return (
    <Box>
      {lines.some(line => line.isInactive) && (
        <Alert severity="warning" sx={{ mb: 2, borderRadius: 2 }}>
          {inactiveNotice ?? t('billing.lines.inactiveNotice')}
        </Alert>
      )}

      {!lines.length ? (
        <Typography sx={{ fontSize: 14, color: MUTED }}>{t('billing.lines.empty')}</Typography>
      ) : (
        <Box sx={{ border: `1px solid ${palette.line}`, borderRadius: '10px', overflow: 'hidden' }}>
          <LineTableHeader />
          <Box component="ul" sx={{ listStyle: 'none', m: 0, p: 0 }}>
            {lines.map(line => {
              const isAttendance = line.type === BillingItemTypeEnum.ATTENDANCE_BASED;
              const label = line.pdfLabelOverride ?? line.catalogLabel;
              const price = line.unitPriceNetOverride ?? line.catalogPrice;
              const special = line.unitPriceNetOverride !== null && line.unitPriceNetOverride !== undefined && line.unitPriceNetOverride !== line.catalogPrice;
              const hintId = `line-${line.billingItemId}-price-hint`;
              const own = allowDiscounts ? discountsOn(line, allDiscounts) : [];
              return (
                <React.Fragment key={line.billingItemId}>
                  <Box
                    component="li"
                    aria-label={line.name}
                    sx={{
                      display: 'grid',
                      gridTemplateColumns: LINE_COLUMNS,
                      columnGap: 1.5,
                      alignItems: 'start',
                      p: '12px 16px',
                      '& + &': { borderTop: `1px solid ${palette.line}` }
                    }}
                  >
                    <Box sx={{ minWidth: 0, pt: 0.5 }}>
                      <Typography sx={{ fontSize: 15, fontWeight: 500, color: 'text.primary' }}>
                        {line.name}
                        {line.isInactive && <Chip size="small" color="warning" variant="outlined" label={t('billing.lines.inactive')} sx={{ ml: 1 }} />}
                      </Typography>
                      <Typography sx={{ fontSize: 12, color: MUTED }}>
                        {[label && label !== line.name ? t('billing.lines.onInvoice', { label }) : null, unitHint(t, line.type)].filter(Boolean).join(' · ')}
                      </Typography>
                    </Box>

                    <Box>
                      <PriceField
                        value={price}
                        fallback={line.catalogPrice}
                        strong={special}
                        ariaLabel={t('billing.lines.priceFor', { name: line.name })}
                        describedBy={hintId}
                        // the catalogue's price typed in again means the line follows the catalogue again
                        onChange={value => setLine(line.billingItemId, { unitPriceNetOverride: value === line.catalogPrice ? null : value })}
                        inputRef={element => {
                          if (element) priceInputs.current.set(line.billingItemId, element);
                          else priceInputs.current.delete(line.billingItemId);
                        }}
                      />
                      <Typography id={hintId} sx={{ fontSize: 12, color: MUTED, mt: 0.5 }}>
                        {special ? (
                          <>
                            {t('billing.lines.specialPrice', { price: formatMoney(line.catalogPrice) })}{' '}
                            <InlineAction onClick={() => setLine(line.billingItemId, { unitPriceNetOverride: null })}>{t('billing.lines.restore')}</InlineAction>
                          </>
                        ) : (
                          t('billing.lines.fromCatalog')
                        )}
                      </Typography>
                      {lineErrors?.[line.billingItemId] && (
                        <Typography role="alert" sx={{ fontSize: 12, color: 'error.main', mt: 0.5, display: 'flex', gap: 0.5, alignItems: 'center' }}>
                          <ErrorOutlineIcon aria-hidden sx={{ fontSize: 14 }} />
                          {lineErrors[line.billingItemId]}
                        </Typography>
                      )}
                    </Box>

                    {isAttendance ? (
                      <Typography sx={{ fontSize: 13, color: MUTED, lineHeight: '44px', textAlign: 'right' }}>{t('billing.lines.perAttendanceDayShort')}</Typography>
                    ) : (
                      <TextField
                        fullWidth
                        type="number"
                        value={line.quantity ?? 1}
                        onChange={event => setLine(line.billingItemId, { quantity: Number(event.target.value) })}
                        slotProps={{
                          htmlInput: {
                            min: 0,
                            step: 'any',
                            'aria-label': t('billing.lines.quantityFor', { name: line.name }),
                            style: { textAlign: 'right', fontVariantNumeric: 'tabular-nums' }
                          }
                        }}
                        sx={fieldSx}
                      />
                    )}

                    <Typography sx={{ fontSize: 15, fontWeight: 600, textAlign: 'right', lineHeight: '44px', fontVariantNumeric: 'tabular-nums', whiteSpace: 'nowrap' }}>
                      {isAttendance ? t('billing.lines.perDayAmount', { amount: formatMoney(price) }) : formatMoney(lineTotals(line, vatApplicable).gross)}
                    </Typography>

                    <Box sx={{ pt: 0.5 }}>
                      <RowActionsMenu
                        name={line.name}
                        ariaLabel={t('billing.lines.moreFor', { name: line.name })}
                        actions={[
                          { label: t('billing.lines.invoiceLabel'), onClick: () => setLabelFor(line) },
                          { label: t('billing.lines.vatRate'), hidden: !vatApplicable, onClick: () => setVatFor(line) },
                          { label: t('discounts.addToLine'), hidden: !allowDiscounts, onClick: () => addDiscount(line) },
                          {
                            label: t('billing.lines.remove'),
                            danger: true,
                            onClick: () => (own.length ? setRemoving(line) : removeLine(line))
                          }
                        ]}
                      />
                    </Box>
                  </Box>
                  {own.map(({ discount, amount }) => (
                    <DiscountRow
                      key={discount.key}
                      discount={discount}
                      onChange={patch => setDiscount(discount.key, patch)}
                      onRemove={() => onDiscountsChange!(allDiscounts.filter(other => other.key !== discount.key))}
                      targets={targets}
                      targetNet={isAttendance ? null : lineNet(line)}
                      targetName={line.name}
                      amount={amount}
                      problem={problems[discount.key]}
                      showErrors={showErrors}
                      labelRef={element => {
                        if (element) discountLabels.current.set(discount.key, element);
                        else discountLabels.current.delete(discount.key);
                      }}
                    />
                  ))}
                </React.Fragment>
              );
            })}
          </Box>
        </Box>
      )}

      <Stack direction="row" spacing={1} sx={{ mt: 1 }}>
        <AddItemMenu available={available} onPick={addLine} />
        {allowDiscounts && lines.length > 0 && (
          <ButtonBase
            onClick={() => addDiscount()}
            sx={{ height: 44, px: 1.5, gap: 0.75, borderRadius: '10px', fontSize: 16, color: 'primary.main', '&:hover': { backgroundColor: TINT } }}
          >
            <RemoveIcon sx={{ fontSize: 20 }} />
            {t('discounts.add')}
          </ButtonBase>
        )}
      </Stack>
      {listError && (
        <Typography id="billing-lines-error" sx={{ fontSize: 12, color: 'error.main', mt: 0.5, display: 'flex', gap: 0.5, alignItems: 'center' }}>
          <ErrorOutlineIcon aria-hidden sx={{ fontSize: 14 }} />
          {listError}
        </Typography>
      )}

      {showTotal && lines.length > 0 && (
        <Stack direction="row" justifyContent="space-between" alignItems="baseline" sx={{ borderTop: `1px solid ${palette.line}`, pt: 2, mt: 1 }}>
          <Typography sx={{ fontSize: 14, color: MUTED }}>
            {monthly.discount > 0
              ? t('billing.lines.monthlyWithDiscount')
              : monthly.perDay.length
                ? t('billing.lines.monthlyWithoutAttendance')
                : t('billing.lines.monthly')}
          </Typography>
          <Typography sx={{ fontSize: 20, fontWeight: 600, fontVariantNumeric: 'tabular-nums' }} data-testid="billing-lines-total">
            {formatMoney(monthly.gross, { withCurrency: true })}
          </Typography>
        </Stack>
      )}

      {labelFor && (
        <LabelDialog
          title={t('billing.lines.labelDialogTitle', { name: labelFor.name })}
          label={t('billing.lines.invoiceLabel')}
          initial={labelFor.pdfLabelOverride ?? ''}
          placeholder={labelFor.catalogLabel}
          hint={t('billing.lines.labelEmptyHint', { label: labelFor.catalogLabel })}
          onClose={() => setLabelFor(null)}
          onSave={value => setLine(labelFor.billingItemId, { pdfLabelOverride: value.trim() && value.trim() !== labelFor.catalogLabel ? value.trim() : null })}
        />
      )}
      {vatFor && (
        <VatDialog
          title={t('billing.lines.vatDialogTitle', { name: vatFor.name })}
          initial={vatFor.vatRateOverride ?? vatFor.catalogVat}
          hint={t('billing.lines.vatAsCatalog', { rate: vatFor.catalogVat === 0 ? t('billing.items.noVat') : `${vatFor.catalogVat} %` })}
          onClose={() => setVatFor(null)}
          // the catalogue's rate picked again means the line follows the catalogue again
          onSave={rate => setLine(vatFor.billingItemId, { vatRateOverride: rate === vatFor.catalogVat ? null : rate })}
        />
      )}
      <GenericInfoModal
        open={!!removing}
        onClose={() => setRemoving(null)}
        onConfirm={() => {
          if (removing) removeLine(removing);
          setRemoving(null);
        }}
        label={t('billing.lines.removeTitle', { name: removing?.name ?? '' })}
        message={
          removing
            ? t('discounts.removeWithLine', {
                item: removing.name,
                discounts: allDiscounts
                  .filter(discount => discount.appliesTo === removing.billingItemId)
                  .map(discount => discount.label.trim() || t('discounts.unnamed'))
                  .join(', ')
              })
            : ''
        }
        confirmLabel={t('billing.lines.remove')}
      />
    </Box>
  );
};

export default BillingLinesEditor;

/** A child's saved discounts as the editor holds them. */
export const toDiscountStates = (
  saved: { label: string; mode: DiscountModeEnum; value: number; appliesToBillingItemId: string }[]
): DiscountState[] =>
  saved.map(discount => ({
    ...newDiscount(discount.appliesToBillingItemId),
    label: discount.label,
    mode: discount.mode,
    valueText: String(discount.value).replace('.', ',')
  }));
